import {
  ChangeEvent,
  useState,
  useMemo,
  SetStateAction,
  Dispatch
} from 'react';
import useDebounce from './useDebounce';

export interface ValidationLogic {
  logic: (value: string) => boolean;
  msg: string;
}

function useTextInput(
  initVal: string,
  validationLogics: ValidationLogic[],
  delay: number,
  errorProcessedWith: any[] = [],
  formatOnChange?: (before: string) => string
): [
  string,
  (e: ChangeEvent<HTMLInputElement>) => void,
  string | undefined,
  Dispatch<SetStateAction<string>>,
  Dispatch<SetStateAction<boolean>>
] {
  const [value, setValue] = useState(initVal);

  const debouncedValue = useDebounce(value, delay);

  const [notTouched, setNotTouched] = useState(true);

  const errMsg = useMemo(() => {
    if (notTouched) {
      return '';
    }

    for (const { logic, msg } of validationLogics) {
      if (logic(debouncedValue)) {
        return msg;
      }
    }
  }, [notTouched, debouncedValue, ...errorProcessedWith]);

  const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(formatOnChange ? formatOnChange(e.target.value) : e.target.value);
    if (notTouched) {
      setNotTouched(false);
    }
  };

  return [value, onChangeValue, errMsg, setValue, setNotTouched];
}

export default useTextInput;
